// Economie: één munteenheid (euro's) en sterren als voortgang.
// - Euro's verdien je met leveringen en besteed je in de garage.
// - Sterren (uit leveringen) bepalen je rang, de wijken, welke upgrades je mag kopen en je lakkleuren.
// De balans wordt bewaakt door economy.test.ts (eerste upgrade in shift 1–2, busje rond shift 5–8).

import { fmtNum, t, type MessageKey } from '../i18n';

export type BoxId = 'geenbak' | 'kleinebak' | 'grotebak';
export type VehicleId = 'scooter' | 'van';

export interface UpgradeDef {
  id: string;
  name: string;
  icon: string;
  /** Prijs in euro's */
  price: number;
  /** Zoveel sterren heb je nodig voordat je hem mag kopen */
  minStars: number;
  description: string;
}

/** Alles wat je in de garage kunt kopen, in de volgorde waarin het vrijkomt. */
export const UPGRADES: readonly UpgradeDef[] = [
  { id: 'box_kleine', get name() { return t('upgrade.box_kleine.name'); }, icon: '📦', price: 60, minStars: 0, get description() { return t('upgrade.box_kleine.desc'); } },
  { id: 'wheels_sport', get name() { return t('upgrade.wheels_sport.name'); }, icon: '🛞', price: 80, minStars: 15, get description() { return t('upgrade.wheels_sport.desc'); } },
  { id: 'engine_turbo', get name() { return t('upgrade.engine_turbo.name'); }, icon: '⚡', price: 100, minStars: 30, get description() { return t('upgrade.engine_turbo.desc'); } },
  { id: 'box_grote', get name() { return t('upgrade.box_grote.name'); }, icon: '🧳', price: 130, minStars: 45, get description() { return t('upgrade.box_grote.desc'); } },
  { id: 'van', get name() { return t('upgrade.van.name'); }, icon: '🚐', price: 240, minStars: 75, get description() { return t('upgrade.van.desc'); } },
];

export const upgradeById = (id: string) => UPGRADES.find((u) => u.id === id);

/** Lakkleuren en hoeveel sterren je ervoor nodig hebt. */
export const PAINTS = [
  { code: '#dc2626', get name() { return t('paint.dc2626' as MessageKey); }, minStars: 0 },
  { code: '#d97706', get name() { return t('paint.d97706' as MessageKey); }, minStars: 10 },
  { code: '#0284c7', get name() { return t('paint.0284c7' as MessageKey); }, minStars: 20 },
  { code: '#059669', get name() { return t('paint.059669' as MessageKey); }, minStars: 35 },
  { code: '#7c3aed', get name() { return t('paint.7c3aed' as MessageKey); }, minStars: 50 },
  { code: '#18181b', get name() { return t('paint.18181b' as MessageKey); }, minStars: 70 },
] as const;

/** Munten op straat zijn kleine bedragen: 1 munt is 20 cent. */
export const STREET_COIN_EURO = 0.2;

/** Hoeveel orders je tegelijk kunt meenemen. */
export function capacityFor(box: BoxId, vehicle: VehicleId): number {
  if (vehicle === 'van') return 4;
  return box === 'grotebak' ? 3 : box === 'kleinebak' ? 2 : 1;
}

/** Vermenigvuldiger op afkoelen van warme producten (lager is beter). */
export function warmLossFactor(box: BoxId, vehicle: VehicleId): number {
  if (vehicle === 'van') return 0.3;
  return box === 'grotebak' ? 0.4 : box === 'kleinebak' ? 0.65 : 1;
}

/** Vermenigvuldiger op smelten van koude producten: een warmhoudbak werkt tegen je, het busje heeft een koelvak. */
export function coldLossFactor(box: BoxId, vehicle: VehicleId): number {
  if (vehicle === 'van') return 0.6;
  return box === 'grotebak' ? 1.6 : box === 'kleinebak' ? 1.3 : 1;
}

/** Vermenigvuldiger op schokschade (lager is beter). */
export function shockFactor(box: BoxId, vehicle: VehicleId): number {
  if (vehicle === 'van') return 0.6;
  return box === 'grotebak' ? 0.55 : box === 'kleinebak' ? 0.75 : 1;
}

export interface Rank {
  rank: number;
  title: string;
  /** Aantal sterren vanaf waar deze rang geldt */
  from: number;
  districts: string[];
}

/** Rangen op basis van het totaal aantal sterren. Elke rang opent wijken verder van de bakkerij. */
export const RANKS: readonly Rank[] = [
  { rank: 1, get title() { return t('rank.1'); }, from: 0, districts: ['dorpskern', 'campus', 'centrum'] },
  { rank: 2, get title() { return t('rank.2'); }, from: 25, districts: ['dorpskern', 'campus', 'centrum', 'haven', 'oost'] },
  { rank: 3, get title() { return t('rank.3'); }, from: 140, districts: ['dorpskern', 'campus', 'centrum', 'haven', 'oost', 'industrie', 'zuid'] },
];

export function rankFor(stars: number): Rank {
  let current = RANKS[0];
  for (const r of RANKS) if (stars >= r.from) current = r;
  return current;
}

export function nextRank(stars: number): Rank | null {
  return RANKS.find((r) => r.from > stars) ?? null;
}

export const DISTRICT_NAMES: Record<string, string> = {
  get dorpskern() { return t('district.dorpskern'); },
  get campus() { return t('district.campus'); },
  get centrum() { return t('district.centrum'); },
  get haven() { return t('district.haven'); },
  get oost() { return t('district.oost'); },
  get industrie() { return t('district.industrie'); },
  get zuid() { return t('district.zuid'); },
};

/** Hoger loon voor wijken verder weg. */
export const DISTRICT_PAY: Record<string, number> = {
  dorpskern: 1,
  campus: 1.1,
  centrum: 1.15,
  haven: 1.25,
  oost: 1.25,
  industrie: 1.35,
  zuid: 1.35,
};

/** Bedrag netjes als euro's, met het decimaalteken van de taal (€12,50 of €12.50). */
export const euro = (n: number) => `€${fmtNum(Math.round(n * 100) / 100, 2)}`;

/** Zonder cijfers achter de komma als het een rond bedrag is (€15 in plaats van €15,00). */
export const euroShort = (n: number) => euro(n).replace(/[.,]00$/, '');
